import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { MoneyInput } from '@/components/money/MoneyInput';
import { formatCOP } from '@/lib/format';
import { apiErrorMessage } from '@/lib/api';
import { useCreatePayment } from '@/hooks/useDebts';
import { monthlyInterestPreview } from './math';
import type { DebtDTO } from '@shared/types';

export function PaymentForm({ debt, onSuccess }: { debt: DebtDTO; onSuccess?: () => void }) {
  const [amount, setAmount] = useState(0);
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const createPayment = useCreatePayment(debt.id);

  const interestPart = Math.min(amount, monthlyInterestPreview(debt));
  const principalPart = Math.max(0, amount - interestPart);

  return (
    <form
      className="space-y-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (amount <= 0) return;
        try {
          await createPayment.mutateAsync({ amount, paidAt, note: null });
          toast.success('Abono registrado');
          setAmount(0);
          onSuccess?.();
        } catch (err) {
          toast.error(apiErrorMessage(err, 'No se pudo registrar el abono'));
        }
      }}
    >
      <div>
        <label className="text-xs font-medium text-muted-foreground">Monto del abono</label>
        <MoneyInput value={amount} onChange={setAmount} />
        {debt.minimumPayment != null && (
          <button
            type="button"
            className="mt-1 text-xs text-primary hover:underline"
            onClick={() => setAmount(debt.minimumPayment!)}
          >
            Usar pago mínimo ({formatCOP(debt.minimumPayment)})
          </button>
        )}
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Fecha</label>
        <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
      </div>
      {amount > 0 && (
        <p className="text-xs text-muted-foreground">
          De tu abono, <span className="text-debt">{formatCOP(interestPart)}</span> van a intereses y{' '}
          <span className="text-income">{formatCOP(principalPart)}</span> a capital.
        </p>
      )}
      <Button type="submit" disabled={amount <= 0 || createPayment.isPending} className="w-full">
        {createPayment.isPending ? 'Registrando…' : 'Registrar abono'}
      </Button>
    </form>
  );
}
